/**
 * Nokosha Public Landing Page
 * Route: / (public, shown to unauthenticated visitors)
 * Design: Uses the app's existing CSS variables (fox orange, warm cream, Inter font)
 * Authenticated users are redirected to /app (dashboard) via AuthGuard on the /app route
 *
 * Sign-in entry points:
 *   1. Desktop nav — ghost "Sign in" button
 *   2. Mobile hamburger menu — ghost "Sign in" button above "Start free"
 *   3. Hero section — "Already have an account? Sign in →" inline link
 */
import {
  Bell,
  Calendar,
  LayoutDashboard,
  List,
  TrendingUp,
  Download,
  Zap,
  Check,
  Menu,
  ChevronRight,
  ArrowRight,
  ArrowLeftRight,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

// ── Asset URLs (CDN-hosted, no local files) ──────────────────────────────────
const FOX_ICON = "/nokosha-fox-icon.png";
const SCREENSHOT_DASHBOARD =
  "https://files.manuscdn.com/user_upload_by_module/session_file/310519663327741972/kCTUJgNlijOiVTlD.webp";
const SCREENSHOT_ALL =
  "https://files.manuscdn.com/user_upload_by_module/session_file/310519663327741972/xfQrGNGVLHmkBTsn.webp";
const SCREENSHOT_CALENDAR =
  "https://files.manuscdn.com/user_upload_by_module/session_file/310519663327741972/xjgtwtQTPlQMtFnk.webp";

// ── Scroll-triggered fade-in hook ────────────────────────────────────────────
function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ── Nav ──────────────────────────────────────────────────────────────────────
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-border bg-background/95 shadow-sm backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <img
            src={FOX_ICON}
            alt="Nokosha fox"
            className="h-8 w-8 rounded-lg"
          />
          <span className="text-xl font-bold text-foreground">Nokosha</span>
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden items-center gap-8 md:flex">
          {[
            ["Features", "#features"],
            ["Pricing", "#pricing"],
            ["FAQ", "#faq"],
          ].map(([label, href]) => (
            <a
              key={label}
              href={href}
              className="text-sm font-medium text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              {label}
            </a>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden items-center gap-3 md:flex">
          <Link to="/auth">
            <Button variant="outline" size="sm" className="rounded-full">
              Sign in
            </Button>
          </Link>
          <Link to="/auth">
            <Button
              size="sm"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Start free
            </Button>
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="p-2 text-foreground md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="flex flex-col gap-4 border-t border-border bg-background px-6 py-4 md:hidden">
          {[
            ["Features", "#features"],
            ["Pricing", "#pricing"],
            ["FAQ", "#faq"],
          ].map(([label, href]) => (
            <a
              key={label}
              href={href}
              className="text-sm font-medium text-foreground"
              onClick={() => setMobileOpen(false)}
            >
              {label}
            </a>
          ))}
          <div className="flex flex-col gap-3 border-t border-border pt-4">
            <Link to="/auth" onClick={() => setMobileOpen(false)}>
              <Button variant="outline" className="w-full rounded-full">
                Sign in
              </Button>
            </Link>
            <Link to="/auth" onClick={() => setMobileOpen(false)}>
              <Button className="w-full rounded-full bg-primary text-primary-foreground">
                Start free
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden pt-16"
      style={{
        background:
          "linear-gradient(135deg, hsl(30 17% 93%) 0%, hsl(28 100% 96%) 55%, hsl(30 17% 93%) 100%)",
      }}
    >
      {/* Decorative blobs */}
      <div
        className="pointer-events-none absolute right-0 top-20 h-[600px] w-[600px] rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(circle, hsl(24 95% 60%) 0%, transparent 70%)",
          transform: "translate(30%, -20%)",
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full opacity-10"
        style={{
          background:
            "radial-gradient(circle, hsl(24 95% 60%) 0%, transparent 70%)",
          transform: "translate(-30%, 30%)",
        }}
      />

      <div className="container relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 py-20 lg:grid-cols-[1fr_1.1fr] lg:gap-16 lg:py-28">
          {/* Copy */}
          <div className="max-w-xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
              <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                Now with free plan
              </span>
            </div>

            <h1 className="mb-6 text-5xl font-bold leading-[1.1] text-foreground lg:text-6xl xl:text-7xl">
              Stop paying for things you{" "}
              <span className="italic text-primary">forgot about.</span>
            </h1>

            <p className="mb-8 text-lg leading-relaxed text-muted-foreground">
              Nokosha gives you one clear view of every subscription — what's
              due, what's draining you, and what to cancel. Free for up to 10
              subscriptions.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/auth">
                <Button
                  size="lg"
                  className="gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Start for free
                  <ArrowRight size={16} />
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline" className="rounded-full">
                  See how it works
                </Button>
              </a>
            </div>

            <p className="mt-4 text-sm text-muted-foreground">
              No credit card required · Free forever up to 10 subscriptions
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link
                to="/auth"
                className="font-semibold text-primary hover:underline"
              >
                Sign in →
              </Link>
            </p>
          </div>

          {/* Hero screenshot */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-2xl"
                style={{
                  background:
                    "linear-gradient(135deg, hsl(24 95% 60% / 0.2), hsl(24 95% 60% / 0.05))",
                  transform: "rotate(4deg) scale(1.02)",
                }}
              />
              <div
                className="relative overflow-hidden rounded-2xl"
                style={{
                  boxShadow:
                    "0 32px 80px hsl(217 9% 23% / 0.2), 0 8px 24px hsl(24 95% 60% / 0.15)",
                  animation: "nokosha-float 5s ease-in-out infinite",
                }}
              >
                <img
                  src={SCREENSHOT_DASHBOARD}
                  alt="Nokosha dashboard showing subscription overview"
                  className="block w-full max-w-[520px]"
                />
              </div>
              {/* Floating stat pill */}
              <div className="absolute -bottom-4 -left-4 flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 shadow-lg">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <TrendingUp size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">2026 Total</p>
                  <p className="font-mono text-sm font-bold text-foreground">
                    €752.59
                  </p>
                </div>
              </div>
              {/* Floating alert pill */}
              <div className="absolute -right-4 -top-4 flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 shadow-lg">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-50">
                  <Bell size={16} className="text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Next payment</p>
                  <p className="text-sm font-bold text-foreground">
                    Notion · in 3 days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes nokosha-float {
          0%, 100% { transform: translateY(0px) rotate(-2deg); }
          50% { transform: translateY(-8px) rotate(-2deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="nokosha-float"] { animation: none; transform: rotate(-2deg); }
        }
      `}</style>
    </section>
  );
}

// ── Features ─────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: LayoutDashboard,
    label: "View 1",
    title: "What needs attention now",
    desc: "Your most urgent subscriptions surface first: payments waiting on your confirmation, renewals coming up, and what you're spending this month, all in one glance. No more surprises on your bank statement.",
    screenshot: SCREENSHOT_DASHBOARD,
    alt: "Nokosha dashboard view",
    annotation: "Pending: 1 payment",
  },
  {
    icon: List,
    label: "View 2",
    title: "The full picture",
    desc: "Every subscription in one sortable, filterable table: what each one costs, your annual projection, and — finally — the total you've been afraid to add up.",
    screenshot: SCREENSHOT_ALL,
    alt: "Nokosha all subscriptions view",
    annotation: "10 subscriptions · €142/mo",
  },
  {
    icon: Calendar,
    label: "View 3",
    title: "Plan ahead",
    desc: "See the whole year at once. Spot the months where payments cluster, plan around big expenses, and never get blindsided by an annual renewal again.",
    screenshot: SCREENSHOT_CALENDAR,
    alt: "Nokosha calendar view",
    annotation: "8 events in August",
  },
];

// ── Feature row sub-component (hooks must be at component top level) ─────────
type FeatureItem = (typeof FEATURES)[number];

function FeatureRow({
  feature,
  index,
}: {
  feature: FeatureItem;
  index: number;
}) {
  const { ref, inView } = useInView();
  const Icon = feature.icon;
  const isEven = index % 2 === 0;
  return (
    <div
      ref={ref}
      className={`grid items-center gap-12 transition-all duration-700 lg:grid-cols-2 ${
        inView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
      }`}
    >
      <div className={isEven ? "lg:order-1" : "lg:order-2"}>
        <span className="mb-4 inline-block border-l-2 border-primary pl-3 text-xs font-semibold uppercase tracking-widest text-primary">
          {feature.label}
        </span>
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
          <Icon size={24} className="text-primary" />
        </div>
        <h3 className="mb-4 text-2xl font-bold text-foreground lg:text-3xl">
          {feature.title}
        </h3>
        <p className="text-lg leading-relaxed text-muted-foreground">
          {feature.desc}
        </p>
      </div>
      {/* Screenshot as warm editorial artifact */}
      <div className={`${isEven ? "lg:order-2" : "lg:order-1"} relative`}>
        <div
          className="absolute inset-0 rounded-2xl bg-primary/5"
          style={{
            transform: `rotate(${isEven ? "3deg" : "-3deg"}) scale(1.03)`,
          }}
        />
        <div
          className="absolute inset-0 rounded-2xl border border-border"
          style={{
            transform: `rotate(${isEven ? "1.5deg" : "-1.5deg"}) scale(1.015)`,
          }}
        />
        <img
          src={feature.screenshot}
          alt={feature.alt}
          className="relative w-full rounded-xl border border-border"
          style={{
            transform: `rotate(${isEven ? "-2deg" : "2deg"})`,
            boxShadow: "0 20px 60px hsl(217 9% 23% / 0.12)",
          }}
        />
        <div className="absolute -bottom-3 right-4 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-md">
          {feature.annotation}
        </div>
      </div>
    </div>
  );
}

function Features() {
  return (
    <section id="features" className="bg-card py-24 lg:py-32">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Asymmetric header */}
        <div className="mb-20 grid gap-8 lg:grid-cols-[200px_1fr]">
          <div className="pt-2">
            <span className="inline-block border-l-2 border-primary pl-3 text-xs font-semibold uppercase tracking-widest text-primary">
              Three views.
              <br />
              One clear picture.
            </span>
          </div>
          <div>
            <h2 className="mb-4 text-4xl font-bold leading-tight text-foreground lg:text-5xl">
              Designed for the way you actually think about money
            </h2>
            <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Most subscription trackers dump everything in a list and call it
              done. Nokosha gives you three purposeful views — each one
              answering a different question.
            </p>
          </div>
        </div>

        {/* Feature rows */}
        <div className="space-y-28">
          {FEATURES.map((feature, i) => (
            <FeatureRow key={feature.title} feature={feature} index={i} />
          ))}
        </div>

        {/* Pro feature pills */}
        <div className="mt-24 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: Bell,
              label: "Renewal alerts",
              desc: "Never miss a trial end date",
            },
            {
              icon: ArrowLeftRight,
              label: "Live currency exchange rates",
              desc: "See every subscription in one currency",
            },
            {
              icon: Zap,
              label: "Budget app sync",
              desc: "Auto-import data straight from YNAB, a popular budgeting app",
            },
            {
              icon: Download,
              label: "Data export",
              desc: "CSV export anytime",
            },
          ].map(({ icon: Icon, label, desc }) => (
            <div
              key={label}
              className="rounded-2xl border border-border bg-background p-5 transition-colors hover:border-primary/40"
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Icon size={20} className="text-primary" />
              </div>
              <p className="mb-1 font-semibold text-foreground">{label}</p>
              <p className="text-sm text-muted-foreground">{desc}</p>
              <Badge className="mt-2 border-green-200 bg-green-100 text-xs text-green-700">
                Pro
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────
function Pricing() {
  const [annual, setAnnual] = useState(false);
  const { ref, inView } = useInView();

  return (
    <section
      id="pricing"
      className="py-24 lg:py-32"
      style={{
        background:
          "linear-gradient(180deg, hsl(30 17% 93%) 0%, hsl(28 100% 96%) 100%)",
      }}
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Asymmetric header */}
        <div
          ref={ref}
          className={`mb-14 grid gap-8 transition-all duration-700 lg:grid-cols-[200px_1fr] ${
            inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="pt-2">
            <span className="inline-block border-l-2 border-primary pl-3 text-xs font-semibold uppercase tracking-widest text-primary">
              Pricing
            </span>
          </div>
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <h2 className="mb-2 text-4xl font-bold text-foreground lg:text-5xl">
                Simple, honest pricing
              </h2>
              <p className="text-lg text-muted-foreground">
                Start free. Upgrade when you're ready. No surprise charges, no
                sneaky upsells.
              </p>
            </div>
            {/* Annual toggle */}
            <div className="flex shrink-0 items-center gap-3">
              <span
                className={`text-sm font-medium ${
                  !annual ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                Monthly
              </span>
              <button
                onClick={() => setAnnual(!annual)}
                className={`relative h-6 w-12 rounded-full transition-colors duration-200 ${
                  annual ? "bg-primary" : "bg-border"
                }`}
                role="switch"
                aria-checked={annual}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
                    annual ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
              <span
                className={`text-sm font-medium ${
                  annual ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                Annual{" "}
                <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
                  Save 40%
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid max-w-3xl gap-6 md:grid-cols-2">
          {/* Free plan */}
          <div className="rounded-3xl border border-border bg-card p-8 shadow-sm">
            <div className="mb-6">
              <Badge variant="outline" className="mb-3">
                Free
              </Badge>
              <div className="flex items-baseline gap-1">
                <span className="font-mono text-5xl font-bold text-foreground">
                  $0
                </span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Free forever. No credit card.
              </p>
            </div>
            <Link to="/auth">
              <Button variant="outline" className="w-full rounded-full">
                Get started free
              </Button>
            </Link>
            <ul className="mt-6 space-y-3">
              {[
                "Up to 10 subscriptions",
                "Dashboard view",
                "All Subscriptions view",
                "Calendar view",
                "Trial end date tracking",
                "Multi-currency display",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-sm text-foreground"
                >
                  <Check size={16} className="mt-0.5 shrink-0 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro plan — forest green premium */}
          <div
            className="relative overflow-hidden rounded-3xl border-2 p-8 shadow-xl"
            style={{
              background: "linear-gradient(135deg, #1C3D2E 0%, #2D6A4F 100%)",
              borderColor: "#2D6A4F",
            }}
          >
            <div
              className="pointer-events-none absolute right-0 top-0 h-40 w-40 rounded-full opacity-15"
              style={{
                background:
                  "radial-gradient(circle, hsl(24 95% 60%), transparent)",
                transform: "translate(30%, -30%)",
              }}
            />
            <div className="relative z-10 mb-6">
              <Badge className="mb-3 bg-primary text-primary-foreground">
                Pro
              </Badge>
              <div className="flex items-baseline gap-1">
                <span className="font-mono text-5xl font-bold text-white">
                  {annual ? "$2.40" : "$3.99"}
                </span>
                <span className="text-sm text-white/60">/month</span>
              </div>
              {annual && (
                <p className="mt-1 text-xs text-white/50">Billed as $29/year</p>
              )}
            </div>
            <Link to="/auth" className="relative z-10 block">
              <Button className="w-full gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                Start Pro free trial
                <ChevronRight size={16} />
              </Button>
            </Link>
            <p className="relative z-10 mt-4 text-sm text-white/60">
              Everything in Free, plus:
            </p>
            <ul className="relative z-10 mt-4 space-y-3">
              {[
                "Unlimited subscriptions",
                "Renewal alerts",
                "Budget app sync (YNAB)",
                "Data export (CSV / JSON)",
                "Full payment history",
                "Live currency exchange rates",
                "Priority support",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-sm text-white"
                >
                  <Check size={16} className="mt-0.5 shrink-0 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="mt-8 text-sm text-muted-foreground">
          All prices in USD · Cancel anytime · Secure payments via Stripe
        </p>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: "Is Nokosha really free?",
    a: "Yes — the free plan is free forever, not a trial. You can track up to 10 subscriptions with the Dashboard, All Subscriptions, and Calendar views at no cost. No credit card required to sign up.",
  },
  {
    q: "What happens when I hit 10 subscriptions on the free plan?",
    a: "You'll see a prompt to upgrade to Pro. Your existing subscriptions stay intact — you just won't be able to add new ones until you upgrade or remove some.",
  },
  {
    q: "What is the YNAB budget sync?",
    a: "You Need A Budget (YNAB) is a popular budgeting app. Nokosha Pro can connect to your YNAB account and automatically import transaction data, so your payment history stays up to date without manual entry.",
  },
  {
    q: "Is my financial data secure?",
    a: "Yes. Your data is encrypted, private, and never sold — full stop. We run our own servers rather than handing your data to a third-party host, so there's no middleman standing between you and your information.",
  },
  {
    q: "Can I export my data?",
    a: "Pro users can export their full subscription list and payment history anytime — as a spreadsheet (CSV), or JSON if you're piping it into your own tools. Your data is always yours.",
  },
  {
    q: "Does Nokosha connect to my bank?",
    a: "Not directly — Nokosha is a manual tracking tool, not a bank aggregator. You add subscriptions yourself, which keeps it simple and privacy-friendly. The YNAB budget sync (Pro) is the closest thing to automatic import.",
  },
];

function FAQ() {
  const { ref, inView } = useInView();
  return (
    <section id="faq" className="bg-card py-24 lg:py-32">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`mb-12 grid gap-8 transition-all duration-700 lg:grid-cols-[200px_1fr] ${
            inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="pt-2">
            <span className="inline-block border-l-2 border-primary pl-3 text-xs font-semibold uppercase tracking-widest text-primary">
              FAQ
            </span>
          </div>
          <div>
            <h2 className="mb-3 text-4xl font-bold text-foreground">
              Common questions
            </h2>
            <p className="text-muted-foreground">
              Everything you need to know before you start tracking.
            </p>
          </div>
        </div>
        <div className="lg:ml-[232px]">
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="overflow-hidden rounded-2xl border border-border bg-background px-6"
              >
                <AccordionTrigger className="py-5 text-left font-semibold text-foreground hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 leading-relaxed text-muted-foreground">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

// ── CTA Banner ────────────────────────────────────────────────────────────────
function CTABanner() {
  const { ref, inView } = useInView();
  return (
    <section
      className="relative overflow-hidden py-20 lg:py-28"
      style={{
        background:
          "linear-gradient(135deg, #1C3D2E 0%, #2D6A4F 60%, #1A1208 100%)",
      }}
    >
      <div className="container relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`grid items-center gap-8 transition-all duration-700 lg:grid-cols-[200px_1fr] ${
            inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="flex justify-center lg:justify-start">
            <img
              src={FOX_ICON}
              alt="Nokosha fox"
              className="h-20 w-20 rounded-2xl shadow-xl"
            />
          </div>
          <div>
            <h2 className="mb-4 text-4xl font-bold text-white lg:text-5xl">
              Your subscriptions are quietly draining you.
            </h2>
            <p className="mb-8 max-w-xl text-lg leading-relaxed text-white/60">
              Nokosha fixes that. Free to start, takes 2 minutes to set up. No
              bank connection needed.
            </p>
            <Link to="/auth">
              <Button
                size="lg"
                className="gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Start for free — no card needed
                <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="border-t border-border bg-background py-12">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
          <div className="flex items-center gap-2.5">
            <img src={FOX_ICON} alt="Nokosha" className="h-7 w-7 rounded-lg" />
            <span className="text-lg font-bold text-foreground">Nokosha</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link
              to="/auth"
              className="transition-colors hover:text-foreground"
            >
              Sign in
            </Link>
            <Link
              to="/auth"
              className="transition-colors hover:text-foreground"
            >
              Sign up
            </Link>
            <a
              href="#pricing"
              className="transition-colors hover:text-foreground"
            >
              Pricing
            </a>
            <a href="#faq" className="transition-colors hover:text-foreground">
              FAQ
            </a>
          </div>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Olombelona. Built with care.
          </p>
        </div>
      </div>
    </footer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { user, loading } = useAuth();

  // Already-authenticated visitors (e.g. PWA launched from a home-screen
  // shortcut) go straight to the dashboard instead of the marketing page.
  if (user && !loading) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="landing-page min-h-screen">
      <Nav />
      <Hero />
      <Features />
      <Pricing />
      <FAQ />
      <CTABanner />
      <Footer />
    </div>
  );
}
